import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { Drawer } from '@motorway/mw-highway-code/pre-alpha';

import { SP_COOKIE_BANNER_EVENTS } from 'Utilities/analytics/analyticsCookieBanner';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { useFeatureFlags } from 'Utilities/hooks';
import { useIsEmbeddedView } from 'Utilities/hooks/useIsEmbeddedView';

import { postConsentDecision } from './consentService';
import { CookieBanner } from './CookieBanner';
import { ACCEPT_ALL_CONSENT, COOKIE_CONSENT_TAKEOVER_EVENT, COOKIE_POLICY_PATH, ESSENTIALS_ONLY_CONSENT } from './cookieConsent.constants';
import { applyConsent, hasStoredConsent } from './cookieConsent.helpers';

import styles from './CookieConsent.module.scss';

export const CookieConsent = () => {
  const isEmbeddedView = useIsEmbeddedView();
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { showMandatoryCookies } = useFeatureFlags();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [takeover, setTakeover] = useState(false);
  const takeoverLabel = takeover ? 'mandatory' : 'optional';

  useEffect(() => {
    setVisible(!hasStoredConsent());
  }, []);

  useEffect(() => {
    const handleTakeover = () => setTakeover(true);
    window.addEventListener(COOKIE_CONSENT_TAKEOVER_EVENT, handleTakeover);
    return () => window.removeEventListener(COOKIE_CONSENT_TAKEOVER_EVENT, handleTakeover);
  }, []);

  useEffect(() => {
    if (visible && !isEmbeddedView) {
      SP_COOKIE_BANNER_EVENTS.LOADED(takeoverLabel);
    }
  }, [pathname, visible, isEmbeddedView, takeoverLabel]);

  const handleAcceptAll = () => {
    SP_COOKIE_BANNER_EVENTS.ACCEPT_ALL_CLICKED(takeoverLabel);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_ACCEPT_ALL_CLICKED);
    applyConsent(ACCEPT_ALL_CONSENT);
    postConsentDecision(ACCEPT_ALL_CONSENT, 'banner_accept_all');
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    SP_COOKIE_BANNER_EVENTS.ESSENTIAL_ONLY_CLICKED(takeoverLabel);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_ESSENTIAL_ONLY_CLICKED);
    applyConsent(ESSENTIALS_ONLY_CONSENT);
    postConsentDecision(ESSENTIALS_ONLY_CONSENT, 'banner_essential_only');
    setVisible(false);
  };

  const handleLearnMore = () => {
    SP_COOKIE_BANNER_EVENTS.LEARN_MORE_CLICKED(COOKIE_POLICY_PATH, takeoverLabel);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_LEARN_MORE_CLICKED);
  };

  if (!visible || isEmbeddedView) {
    return null;
  }

  if (takeover && showMandatoryCookies) {
    return (
      <Drawer
        isOpen
        responsive
        aria-labelledby="cookie-consent-title"
        closeOnBackdropClick={false}
        closeOnEsc={false}
        data-thc-drawer="cookie"
        dismissible={false}
        id="cookie-consent-takeover"
        onClose={() => {}}
        showCloseButton={false}
        showHandle={false}
      >
        <CookieBanner
          takeover
          onAcceptAll={handleAcceptAll}
          onEssentialOnly={handleEssentialOnly}
          onLearnMore={handleLearnMore}
        />
      </Drawer>
    );
  }

  return (
    <div className={styles.overlay}>
      <CookieBanner
        onAcceptAll={handleAcceptAll}
        onEssentialOnly={handleEssentialOnly}
        onLearnMore={handleLearnMore}
      />
    </div>
  );
};
